import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import { 
  VolunteerActivism as SupportMainIcon,
  Close as CloseIcon,
  FavoriteBorder as SupportIcon,
  Share as ShareIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';

/**
 * FloatingActionButton Component
 * Modern FAB with smooth animations using Framer Motion
 */
const FloatingActionButton = ({
  currentView,
  onViewToggle,
  onSupport,
  onShare,
  monthName,
  centerName,
  eventCount,
  theme
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldPulse, setShouldPulse] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const menuRef = useRef(null);
  
  // Check if we're on mobile
  const isMobile = window.innerWidth <= 768;
  
  // Add pulse animation every 5 seconds when not expanded
  useEffect(() => {
    if (!isExpanded) {
      const interval = setInterval(() => {
        setShouldPulse(true);
        setTimeout(() => setShouldPulse(false), 600);
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [isExpanded]);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };
    
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isExpanded]);
  
  // Handle share functionality with screenshot
  const handleShare = async () => {
    setIsCapturing(true);
    
    try {
      // Find the calendar section (includes month header + grid)
      const calendarSection = document.querySelector('[data-calendar-section]') ||
                             document.querySelector('[data-calendar-grid]') || 
                             document.querySelector('.calendar-grid') || 
                             document.querySelector('[class*="calendarContainer"]');
      
      let file = null;
      
      // Try to capture calendar if element exists
      if (calendarSection) {
        try {
          // Show the screenshot header temporarily
          const screenshotHeader = document.querySelector('[data-screenshot-header]');
          if (screenshotHeader) {
            screenshotHeader.style.display = 'block';
          }
          
          // Small delay to ensure rendering
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Capture the calendar section as canvas
          const canvas = await html2canvas(calendarSection, {
            backgroundColor: '#ffffff',
            scale: 2, // Higher quality
            logging: false,
            useCORS: true,
            allowTaint: true
          });
          
          // Hide the screenshot header again
          if (screenshotHeader) {
            screenshotHeader.style.display = 'none';
          }
          
          // Convert canvas to blob
          const blob = await new Promise(resolve => 
            canvas.toBlob(resolve, 'image/png')
          );
          
          // Create file from blob
          if (blob) {
            file = new File([blob], `${centerName}-${monthName}-calendar.png`, { 
              type: 'image/png' 
            });
          }
        } catch (captureErr) {
          console.log('Could not capture calendar:', captureErr);
          // Continue without image
        }
      }
      
      const shareData = {
        title: `${centerName} - ${monthName} Calendar`,
        text: `Check out the ${eventCount} programs this month at ${centerName}`,
        url: window.location.href
      };
      
      // Check if we can share files and have a file to share
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        // Share with image
        await navigator.share({
          ...shareData,
          files: [file]
        });
      } else if (navigator.share && isMobile) {
        // Share without image (fallback)
        await navigator.share(shareData);
      } else {
        // Desktop fallback: download image if available, copy link
        if (file) {
          // Download the image
          const url = URL.createObjectURL(file);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          // Copy link and notify
          await navigator.clipboard.writeText(shareData.url);
          alert('Calendar image downloaded and link copied to clipboard!');
        } else {
          // Just copy link
          await navigator.clipboard.writeText(shareData.url);
          alert('Calendar link copied to clipboard!');
        }
      }
    } catch (err) {
      console.error('Error sharing:', err);
      // Fallback to simple share
      try {
        if (navigator.share) {
          await navigator.share({
            title: `${centerName} - ${monthName} Calendar`,
            text: `Check out the ${eventCount} programs this month`,
            url: window.location.href
          });
        } else {
          await navigator.clipboard.writeText(window.location.href);
          alert('Calendar link copied to clipboard!');
        }
      } catch (fallbackErr) {
        console.error('Fallback share also failed:', fallbackErr);
      }
    } finally {
      setIsCapturing(false);
      setIsExpanded(false);
    }
  };
  
  // Menu items configuration
  const menuItems = [
    {
      icon: <SupportIcon />,
      label: `Support ${monthName} Programs`,
      action: () => {
        onSupport();
        setIsExpanded(false);
      },
      primary: true,
      color: '#ec4899' // Pink color for support
    },
    {
      icon: isCapturing ? (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <ShareIcon />
        </motion.div>
      ) : (
        <ShareIcon />
      ),
      label: isCapturing ? 'Capturing...' : 'Share Calendar',
      action: handleShare,
      color: '#10b981', // Green for share
      disabled: isCapturing
    }
  ];
  
  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };
  
  const menuVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.8,
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { 
      opacity: 0, 
      x: 50,
      scale: 0.8
    },
    visible: { 
      opacity: 1, 
      x: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };
  
  const fabVariants = {
    closed: { 
      rotate: 0,
      scale: 1
    },
    open: { 
      rotate: 45,
      scale: 1.1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    },
    pulse: {
      scale: [1, 1.15, 1],
      boxShadow: [
        '0 4px 12px rgba(0,0,0,0.15)',
        '0 8px 25px rgba(0,0,0,0.25)',
        '0 4px 12px rgba(0,0,0,0.15)'
      ],
      transition: {
        duration: 0.6,
        ease: "easeInOut"
      }
    },
    normal: {
      scale: 1
    }
  };
  
  // Styles
  const styles = {
    container: {
      position: 'fixed',
      bottom: isMobile ? '70px' : '24px',
      right: '20px',
      zIndex: 1000
    },
    fab: {
      width: '56px',
      height: '56px',
      borderRadius: '50%',
      backgroundColor: theme?.colors?.primary || '#0f172a',
      color: 'white',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '24px',
      position: 'relative',
      zIndex: 10,
      outline: 'none',
      boxShadow: isExpanded 
        ? '0 10px 30px rgba(0,0,0,0.3)' 
        : '0 4px 12px rgba(0,0,0,0.15)'
    },
    fabHover: {
      boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
    },
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      zIndex: 1
    },
    menu: {
      position: 'absolute',
      bottom: '70px',
      right: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      zIndex: 5
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 20px',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      borderRadius: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      fontSize: '14px',
      fontWeight: '500',
      color: '#1f2937',
      minWidth: '180px',
      border: '1px solid rgba(255, 255, 255, 0.5)',
      outline: 'none',
      position: 'relative',
      overflow: 'hidden'
    },
    menuItemPrimary: {
      background: `linear-gradient(135deg, ${theme?.colors?.primary || '#0f172a'} 0%, ${theme?.colors?.primaryDark || '#1e293b'} 100%)`,
      color: 'white',
      border: 'none'
    },
    menuItemIcon: {
      fontSize: '20px',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px'
    }
  };
  
  return (
    <div ref={menuRef} style={styles.container}>
      {/* Backdrop */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            style={styles.backdrop} 
            onClick={() => setIsExpanded(false)}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={backdropVariants}
            transition={{ duration: 0.2 }}
          />
        )}
      </AnimatePresence>
      
      {/* Menu Items */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            style={styles.menu}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={menuVariants}
          >
            {menuItems.map((item, index) => (
              <motion.button
                key={index}
                style={{
                  ...styles.menuItem,
                  ...(item.primary ? styles.menuItemPrimary : {}),
                  ...(item.disabled ? { opacity: 0.6, cursor: 'wait' } : {})
                }}
                variants={itemVariants}
                whileHover={item.disabled ? {} : { 
                  scale: 1.05,
                  x: -5,
                  boxShadow: '0 6px 25px rgba(0,0,0,0.12)',
                  transition: { type: "spring", stiffness: 400, damping: 25 }
                }}
                whileTap={item.disabled ? {} : { scale: 0.98 }}
                onClick={item.disabled ? undefined : item.action}
                disabled={item.disabled}
              >
                <motion.div 
                  style={{ 
                    ...styles.iconWrapper,
                    color: item.primary ? 'white' : (item.color || theme?.colors?.primary || '#0f172a')
                  }}
                  whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  {item.icon}
                </motion.div>
                <span>{item.label}</span>
                
                {/* Ripple effect on tap */}
                <motion.div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '100%',
                    height: '100%',
                    borderRadius: '16px',
                    backgroundColor: item.primary ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none'
                  }}
                  initial={{ scale: 0, opacity: 0 }}
                  whileTap={{ scale: 2, opacity: [0, 1, 0] }}
                  transition={{ duration: 0.5 }}
                />
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Main FAB Button */}
      <motion.button
        style={styles.fab}
        onClick={() => setIsExpanded(!isExpanded)}
        variants={fabVariants}
        initial="closed"
        animate={[
          isExpanded ? "open" : "closed",
          shouldPulse ? "pulse" : "normal"
        ]}
        whileHover={{ 
          scale: 1.1,
          boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
          filter: 'brightness(1.1)'
        }}
        whileTap={{ scale: 0.95 }}
        aria-label="Share calendar and support programs"
        title="Share & Support Options"
      >
        <motion.div
          initial={false}
          animate={{ rotate: isExpanded ? 45 : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isExpanded ? (
            <CloseIcon />
          ) : (
            <motion.div
              animate={shouldPulse ? { rotate: [0, 10, -10, 10, 0] } : {}}
              transition={{ duration: 0.6 }}
            >
              <AutoAwesomeIcon style={{ fontSize: 24 }} />
            </motion.div>
          )}
        </motion.div>
      </motion.button>
    </div>
  );
};

export default FloatingActionButton;